import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import { DollarSign, TrendingUp, Heart, ArrowUpRight, Loader2, Download, Gift, Plus, Trash2, Users } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { apiClient } from '../../api/client';
import CostOfGoodsTile from '../../components/admin/CostOfGoodsTile';

const AdminRevenue = () => {
  const [revenue, setRevenue] = useState(null);
  const [subs, setSubs] = useState([]);
  const [licenceTotals, setLicenceTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  const load = useCallback(async () => {
    try {
      const [rev, subData, licData] = await Promise.all([
        apiClient.request('/api/admin/revenue'),
        apiClient.request('/api/admin/subscriptions?limit=50'),
        apiClient.request('/api/school-licensing/admin/licenses').catch(() => null),
      ]);
      setRevenue(rev);
      setSubs(subData.subscriptions || []);
      setLicenceTotals(licData?.totals || null);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    const data = await apiClient.request('/api/admin/export/subscriptions?format=csv');
    const blob = new Blob([typeof data === 'string' ? data : JSON.stringify(data)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'hibff_subscriptions.csv'; a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <AdminLayout pageTitle="Revenue & Subscriptions">
      <CheatSheet pageId="revenue" /><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#F5831F' }} /></div></AdminLayout>;

  return (
    <AdminLayout pageTitle="Revenue & Subscriptions">
      {/* Top Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {[
          { label: 'MRR', value: `£${revenue?.mrr || 0}`, color: '#2EAA5C' },
          { label: 'ARR', value: `£${revenue?.arr || 0}`, color: '#2563EB' },
          { label: 'ARPU', value: revenue?.mrr > 0 ? `£${(revenue.mrr / (revenue.subscribers?.plus_monthly + revenue.subscribers?.plus_annual || 1)).toFixed(2)}` : '£0', color: '#7B4FD8' },
          { label: 'Hearts Revenue', value: `${revenue?.hearts_transactions || 0} txns`, color: '#F5831F' },
          { label: 'Licences Invoiced', value: `£${((((licenceTotals?.outstanding_cents || 0) + (licenceTotals?.collected_cents || 0)) / 100)).toLocaleString()}`, sub: `${licenceTotals?.count || 0} school licence${(licenceTotals?.count || 0) === 1 ? '' : 's'}`, color: '#0F8A4B', testid: 'licence-invoiced-tile' },
          { label: 'Licences Collected', value: `£${(((licenceTotals?.collected_cents || 0) / 100)).toLocaleString()}`, sub: `£${(((licenceTotals?.outstanding_cents || 0) / 100)).toLocaleString()} outstanding`, color: '#059669', testid: 'licence-collected-tile' },
        ].map((m, i) => (
          <div key={i} className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid={m.testid}>
            <p className="text-xs" style={{ color: '#44403A' }}>{m.label}</p>
            <p className="text-3xl font-bold mt-1" style={{ color: '#1A1209' }}>{m.value}</p>
            {m.sub && <p className="text-[11px] mt-1" style={{ color: m.color }}>{m.sub}</p>}
          </div>
        ))}
      </div>

      {revenue?.mrr === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <DollarSign size={20} className="text-emerald-600" />
          <div>
            <p className="text-sm font-medium text-emerald-800">Payments are LIVE — no active paid subscriptions right now</p>
            <p className="text-xs text-emerald-600">Stripe (web) and Google Play Billing (Android) are both active and have processed real transactions (first payment was the founder's own, since refunded). Metrics populate automatically with each paid subscription.</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4">
        {['overview', 'subscriptions', 'gifted', 'churn'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium capitalize transition-colors ${tab === t ? 'bg-white shadow-sm' : 'text-gray-500'}`}
            style={tab === t ? { color: '#1A1209' } : {}}>{t === 'gifted' ? 'Gift Plans' : t}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Plan Breakdown</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between p-2 rounded" style={{ backgroundColor: '#F9F9F9' }}><span>Paid plans (total)</span><span className="font-bold">{revenue?.subscribers?.plus_monthly !== undefined ? (revenue.subscribers.plus_monthly + revenue.subscribers.plus_annual) : 0} paid</span></div>
              <div className="flex justify-between p-2 rounded" style={{ backgroundColor: '#F9F9F9' }}><span>Together (£28/mo)</span><span className="font-bold">{revenue?.subscribers?.plus_monthly || 0}</span></div>
              <div className="flex justify-between p-2 rounded" style={{ backgroundColor: '#F9F9F9' }}><span>Brave (£188/yr)</span><span className="font-bold">{revenue?.subscribers?.plus_annual || 0}</span></div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={[{ name: 'Free', value: Math.max(1, 5) }, { name: 'Plus', value: revenue?.subscribers?.plus_monthly || 0 }, { name: 'Annual', value: revenue?.subscribers?.plus_annual || 0 }]} dataKey="value" cx="50%" cy="50%" outerRadius={70} innerRadius={40} label>
                  <Cell fill="#E5E7EB" /><Cell fill="#7B4FD8" /><Cell fill="#F5831F" />
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {tab === 'subscriptions' && (
        <div className="bg-white rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <div className="p-4 flex items-center justify-between border-b" style={{ borderColor: '#E0E0E0' }}>
            <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Active Subscriptions</h3>
            <button onClick={handleExport} className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 text-white" style={{ backgroundColor: '#F5831F' }}><Download size={12} /> Export</button>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr style={{ backgroundColor: '#F9F9F9' }}>
              {['User ID', 'Plan', 'Status', 'Started', 'Amount'].map(h => <th key={h} className="text-left p-3 text-xs font-medium whitespace-nowrap" style={{ color: '#44403A' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {subs.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-sm" style={{ color: '#44403A' }}>No active paid subscriptions — payments are live (Stripe + Google Play).</td></tr>
              ) : subs.map((s, i) => (
                <tr key={i} className="border-t" style={{ borderColor: '#E0E0E0' }}>
                  <td className="p-3 text-xs" style={{ color: '#44403A' }}>{s.user_id?.slice(0, 15)}...</td>
                  <td className="p-3 capitalize">{s.plan_id}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 text-xs rounded-full ${s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{s.status}</span></td>
                  <td className="p-3 text-xs">{s.created_at?.slice(0, 10)}</td>
                  <td className="p-3 font-medium">{s.plan_id === 'plus_monthly' ? '£28' : s.plan_id === 'plus_annual' ? '£188' : '£0'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === 'churn' && (
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Churn Analysis</h3>
          <p className="text-sm" style={{ color: '#44403A' }}>HiBFF is a membership for life — churn is a warning signal, never a success. Goal-complete leavers are tracked for priority win-back outreach.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div className="p-4 rounded-lg bg-green-50 border border-green-200">
              <p className="text-sm font-medium text-green-800">Goal-Complete Leavers</p>
              <p className="text-2xl font-bold text-green-700 mt-1">0</p>
              <p className="text-xs text-green-600">Completed journey before going inactive — win-back priority</p>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#F9F9F9', border: '1px solid #E0E0E0' }}>
              <p className="text-sm font-medium" style={{ color: '#44403A' }}>Standard Churn</p>
              <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>0</p>
              <p className="text-xs" style={{ color: '#44403A' }}>Cancelled, no review</p>
            </div>
          </div>
        </div>
      )}

      {tab === 'gifted' && <GiftPlansTab />}

      {/* Cost of Goods estimator — usage-based spend across AI / SMS / email / video */}
      <div className="mt-6">
        <CostOfGoodsTile />
      </div>
    </AdminLayout>
  );
};

// ─── GIFT PLANS TAB ──────────────────────────────────────────────────────────

const GiftPlansTab = () => {
  const [gifts, setGifts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', recipient_name: '', org_name: '', reason: '', plan: 'plus_monthly' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchGifts = async () => {
    try {
      const d = await apiClient.request('/api/admin/gifted-subscriptions');
      setGifts(d.gifts || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchGifts(); }, []);

  const handleGift = async () => {
    if (!form.email.trim()) return;
    setResult(null);
    try {
      const res = await apiClient.request('/api/admin/subscriptions/gift', { method: 'POST', body: JSON.stringify(form) });
      setResult(res);
      if (res.success) {
        setShowForm(false);
        setForm({ email: '', recipient_name: '', org_name: '', reason: '', plan: 'plus_monthly' });
        fetchGifts();
      }
    } catch (e) { setResult({ success: false, message: 'Failed to gift subscription' }); }
  };

  const handleRevoke = async (giftId) => {
    await apiClient.request(`/api/admin/gifted-subscriptions/${giftId}`, { method: 'DELETE' });
    fetchGifts();
  };

  const active = gifts.filter(g => g.status === 'active');
  const revoked = gifts.filter(g => g.status !== 'active');

  return (
    <div className="space-y-4" data-testid="gift-plans-tab">
      {/* Info Banner */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-5 flex items-start gap-3">
        <Gift size={20} className="text-purple-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-purple-900">Gift Free Subscriptions</p>
          <p className="text-xs text-purple-700 mt-0.5">Gift a free-forever HiBFF Plus monthly subscription to individuals or organisations who would benefit from using HiBFF. If the user hasn&apos;t registered yet, the gift will activate automatically when they sign up.</p>
        </div>
      </div>

      <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 text-white text-sm font-semibold rounded-xl" style={{ backgroundColor: '#7B4FD8' }} data-testid="gift-plan-btn">
        <Plus size={14} /> Gift a Plan
      </button>

      {showForm && (
        <div className="bg-white rounded-xl p-5" style={{ border: '2px solid #7B4FD8' }} data-testid="gift-form">
          <h4 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>New Gift Subscription</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium text-gray-500 uppercase">Email Address *</label>
              <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="user@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="gift-email" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-gray-500 uppercase">Recipient Name</label>
              <input type="text" value={form.recipient_name} onChange={e => setForm(p => ({ ...p, recipient_name: e.target.value }))} placeholder="John Doe"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="gift-name" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-gray-500 uppercase">Organisation (optional)</label>
              <input type="text" value={form.org_name} onChange={e => setForm(p => ({ ...p, org_name: e.target.value }))} placeholder="School / Company name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="gift-org" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-gray-500 uppercase">Plan</label>
              <select value={form.plan} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="gift-plan-select">
                <option value="plus_monthly">Together (Free Forever)</option>
                <option value="plus_annual">Brave (Free Forever)</option>
              </select>
            </div>
          </div>
          <div className="mt-3">
            <label className="text-[10px] font-medium text-gray-500 uppercase">Reason for Gifting</label>
            <input type="text" value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} placeholder="e.g. Beta tester, school pilot partner, supporter"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="gift-reason" />
          </div>
          {result && (
            <p className={`text-xs mt-2 ${result.success ? 'text-green-600' : 'text-red-500'}`} data-testid="gift-result">{result.message}</p>
          )}
          <div className="flex gap-2 mt-3">
            <button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button>
            <button onClick={handleGift} disabled={!form.email.trim()} className="flex-1 py-2 text-white font-semibold rounded-lg text-sm disabled:opacity-50" style={{ backgroundColor: '#7B4FD8' }} data-testid="gift-submit-btn">
              Gift Free Subscription
            </button>
          </div>
        </div>
      )}

      {/* Active Gifts */}
      {active.length > 0 && (
        <div className="bg-white rounded-xl" style={{ border: '1px solid #E0E0E0' }}>
          <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: '#E0E0E0' }}>
            <h4 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}><Gift size={14} className="text-purple-600" /> Active Gift Plans ({active.length})</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr style={{ backgroundColor: '#F9F9F9' }}>
                {['Recipient', 'Email', 'Organisation', 'Reason', 'Gifted On', ''].map(h => <th key={h} className="text-left p-3 text-[10px] font-medium uppercase whitespace-nowrap" style={{ color: '#44403A' }}>{h}</th>)}
              </tr></thead>
              <tbody>{active.map((g, i) => (
                <tr key={i} className="border-t" style={{ borderColor: '#E0E0E0' }} data-testid={`gift-${g.gift_id}`}>
                  <td className="p-3">
                    <p className="font-medium text-sm" style={{ color: '#1A1209' }}>{g.recipient_name || '-'}</p>
                    {g.user_id ? <span className="text-[10px] text-green-600">Registered</span> : <span className="text-[10px] text-amber-600">Pending signup</span>}
                  </td>
                  <td className="p-3 text-xs" style={{ color: '#44403A' }}>{g.email}</td>
                  <td className="p-3 text-xs" style={{ color: '#44403A' }}>{g.org_name || '-'}</td>
                  <td className="p-3 text-xs" style={{ color: '#44403A' }}>{g.reason || '-'}</td>
                  <td className="p-3 text-xs" style={{ color: '#44403A' }}>{g.created_at?.slice(0, 10)}</td>
                  <td className="p-3"><button onClick={() => handleRevoke(g.gift_id)} className="text-red-400 hover:text-red-600" data-testid={`revoke-gift-${g.gift_id}`}><Trash2 size={14} /></button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Revoked */}
      {revoked.length > 0 && (
        <div>
          <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Revoked ({revoked.length})</h4>
          {revoked.map((g, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs text-gray-400 mb-1">
              <span>{g.email}</span><span>{g.revoked_at?.slice(0, 10) || 'Revoked'}</span>
            </div>
          ))}
        </div>
      )}

      {gifts.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-400 text-sm">No gifted subscriptions yet. Use the button above to gift your first free plan.</div>
      )}
    </div>
  );
};

export default AdminRevenue;
